import { useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { Divider } from "primereact/divider";
import { Avatar } from "primereact/avatar";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import BranchService from "../../services/BranchService";
import { OverlayPanel } from "primereact/overlaypanel";
import { Dialog } from "primereact/dialog";
import NotificationService from "../../services/NotificationService";

const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || "";
};

export default function HeaderBar({ onSidebarToggle, onSettingsToggle }) {
  const { user, setToken, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  const [notifications, setNotifications] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_notifications");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [notificationDialog, setNotificationDialog] = useState(false);
  const op = useRef(null);

  const notificationList = Array.isArray(notifications) ? notifications : [];

  const fetchNotifications = async (force = false) => {
    try {
      const now = Date.now();
      const lastFetch = localStorage.getItem("last_notification_fetch");
      const twentyFourHours = 24 * 60 * 60 * 1000;

      if (!force && lastFetch && (now - parseInt(lastFetch, 10)) < twentyFourHours) {
        return;
      }

      const res = await NotificationService.getUserNotifications();
      if (res?.data?.success) {
        const data = res.data.data || [];
        setNotifications(data);
        localStorage.setItem("cached_notifications", JSON.stringify(data));
        localStorage.setItem("last_notification_fetch", now.toString());
      } else {
        setNotifications([]);
        localStorage.removeItem("cached_notifications");
      }
    } catch (err) {
      console.error("Bildirimler yüklenemedi", err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications(false);

      const handleNotificationSent = () => {
        fetchNotifications(true);
      };

      window.addEventListener("notification-sent", handleNotificationSent);
      return () => {
        window.removeEventListener("notification-sent", handleNotificationSent);
      };
    }
  }, [user]);

  const handleNotificationClick = async (n) => {
    setSelectedNotification(n);
    setNotificationDialog(true);
    op.current.hide();

    if (!n.isRead) {
      try {
        await NotificationService.markAsRead(n.id);
        setNotifications(prev => {
          const arr = Array.isArray(prev) ? prev : [];
          const updated = arr.map(item => item.id === n.id ? { ...item, isRead: true } : item);
          localStorage.setItem("cached_notifications", JSON.stringify(updated));
          return updated;
        });
      } catch (err) {
        console.error("Bildirim okundu olarak işaretlenemedi", err);
      }
    }
  };

  const markAllAsRead = async () => {
    const unread = notificationList.filter(n => !n.isRead);
    if (unread.length === 0) return;
    try {
      await Promise.all(unread.map(n => NotificationService.markAsRead(n.id)));
      setNotifications(prev => {
        const arr = Array.isArray(prev) ? prev : [];
        const updated = arr.map(item => ({ ...item, isRead: true }));
        localStorage.setItem("cached_notifications", JSON.stringify(updated));
        return updated;
      });
    } catch (err) {
      console.error("Tüm bildirimler okundu işaretlenemedi", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const pathname = location.pathname;

  const getBreadcrumbs = () => {
    if (pathname.startsWith("/customers/detail")) {
      return [
        { label: "Müşteriler", path: "/customers" },
        { label: "Müşteri Detayı" }
      ];
    }
    if (pathname.startsWith("/suppliers/detail")) {
      return [
        { label: "Tedarikçi Listesi", path: "/suppliers" },
        { label: "Tedarikçi Detayı" }
      ];
    }
    if (pathname.startsWith("/products/price-tiers")) {
      if (pathname.includes("/add")) {
        return [
          { label: "Ürünler", path: "/products" },
          { label: "Fiyat Listesi", path: "/products/price-tiers" },
          { label: "Yeni Fiyat Listesi Ekle" }
        ];
      }
      if (pathname.includes("/edit")) {
        return [
          { label: "Ürünler", path: "/products" },
          { label: "Fiyat Listesi", path: "/products/price-tiers" },
          { label: "Fiyat Listesini Düzenle" }
        ];
      }
      return [
        { label: "Ürünler", path: "/products" },
        { label: "Fiyat Listesi" }
      ];
    }
    if (pathname.startsWith("/products")) {
      if (pathname.includes("/detail")) {
        return [
          { label: "Ürünler", path: "/products" },
          { label: "Ürün Detayı" }
        ];
      }
      if (pathname.includes("/edit")) {
        return [
          { label: "Ürünler", path: "/products" },
          { label: "Ürün Düzenle" }
        ];
      }
      if (pathname.includes("/add")) {
        return [
          { label: "Ürünler", path: "/products" },
          { label: "Yeni Ürün Ekle" }
        ];
      }
      return [{ label: "Ürün Listesi" }];
    }
    if (pathname.startsWith("/sales")) {
      if (pathname.includes("/productsale")) {
        return [
          { label: "Satışlar", path: "/sales" },
          { label: "Ürün Satışı" }
        ];
      }
      if (pathname.includes("/detail")) {
        return [
          { label: "Satışlar", path: "/sales" },
          { label: "Satış Detayı" }
        ];
      }
      return [{ label: "Satış Listesi" }];
    }
    if (pathname.startsWith("/suppliers/purchase")) {
      return [
        { label: "Tedarikçi Listesi", path: "/suppliers" },
        { label: "Alış Faturası Girişi" }
      ];
    }
    if (pathname.startsWith("/suppliers/add")) {
      return [
        { label: "Tedarikçi Listesi", path: "/suppliers" },
        { label: "Yeni Tedarikçi" }
      ];
    }
    if (pathname.startsWith("/suppliers/edit")) {
      return [
        { label: "Tedarikçi Listesi", path: "/suppliers" },
        { label: "Tedarikçi Düzenle" }
      ];
    }
    if (pathname.startsWith("/suppliers")) {
      return [{ label: "Tedarikçi Listesi" }];
    }
    if (pathname.startsWith("/customers")) {
      return [{ label: "Müşteri Listesi" }];
    }
    if (pathname.startsWith("/stock/movements")) {
      return [{ label: "Stok Hareketleri" }];
    }
    if (pathname.startsWith("/stock/adjustment")) {
      return [{ label: "Hızlı Stok Giriş/Çıkış" }];
    }
    if (pathname.startsWith("/stock/critical")) {
      return [{ label: "Kritik Stok Takibi" }];
    }
    if (pathname.startsWith("/warehouses")) {
      return [{ label: "Depo Listesi" }];
    }
    if (pathname.startsWith("/sales/retail")) {
      return [
        { label: "Satışlar", path: "/sales" },
        { label: "Perakende Satış Listesi" }
      ];
    }
    if (pathname.startsWith("/quotations/add")) {
      return [
        { label: "Tekliflerim", path: "/quotations" },
        { label: "Yeni Teklif Oluştur" }
      ];
    }
    if (pathname.startsWith("/quotations/edit")) {
      return [
        { label: "Tekliflerim", path: "/quotations" },
        { label: "Teklif Düzenle" }
      ];
    }
    if (pathname.startsWith("/quotations")) {
      return [{ label: "Tekliflerim" }];
    }
    if (pathname.startsWith("/returns")) {
      return [{ label: "İade Listesi" }];
    }
    if (pathname.startsWith("/employees/add")) {
      return [
        { label: "Çalışan Listesi", path: "/employees" },
        { label: "Yeni Çalışan Ekle" }
      ];
    }
    if (pathname.startsWith("/employees/edit")) {
      return [
        { label: "Çalışan Listesi", path: "/employees" },
        { label: "Çalışan Düzenle" }
      ];
    }
    if (pathname.startsWith("/employees/detail")) {
      return [
        { label: "Çalışan Listesi", path: "/employees" },
        { label: "Çalışan Detayı" }
      ];
    }
    if (pathname.startsWith("/employees")) {
      return [{ label: "Çalışan Listesi" }];
    }
    if (pathname.startsWith("/reports/sales")) {
      return [{ label: "Satış Raporu" }];
    }
    if (pathname.startsWith("/reports/expenses")) {
      return [{ label: "Gider Raporu" }];
    }
    if (pathname.startsWith("/reports/profit-loss")) {
      return [{ label: "Kar/Zarar Analizi" }];
    }
    if (pathname.startsWith("/reports/stock")) {
      return [{ label: "Stok Raporu" }];
    }
    if (pathname.startsWith("/reports/customer-balance")) {
      return [{ label: "Müşteri Bakiyeleri" }];
    }
    if (pathname.startsWith("/reports/branch")) {
      return [{ label: "Şube Raporu" }];
    }
    if (pathname.startsWith("/reports/account-transactions")) {
      return [{ label: "Hesap Hareketleri Raporu" }];
    }
    if (pathname.startsWith("/categories/add")) {
      return [
        { label: "Kategoriler", path: "/categories" },
        { label: "Kategori Ekle" }
      ];
    }
    if (pathname.startsWith("/categories/edit")) {
      return [
        { label: "Kategoriler", path: "/categories" },
        { label: "Kategori Düzenle" }
      ];
    }
    if (pathname.startsWith("/categories")) {
      return [{ label: "Kategoriler" }];
    }
    if (pathname.startsWith("/settings/print-templates")) {
      return [{ label: "Yazdırma Tasarımları" }];
    }
    if (pathname.startsWith("/settings/quotation-template")) {
      return [{ label: "Teklif Tasarımı" }];
    }
    if (pathname.startsWith("/settings/view-settings")) {
      return [{ label: "Görünüm Ayarları" }];
    }
    if (pathname.startsWith("/settings/branches")) {
      return [{ label: "Şube Yönetimi" }];
    }
    if (pathname.startsWith("/expenses/categories/add")) {
      return [
        { label: "Gider Listesi", path: "/expenses" },
        { label: "Gider Başlıkları", path: "/expenses/categories" },
        { label: "Gider Başlığı Ekle" }
      ];
    }
    if (pathname.startsWith("/expenses/categories/edit")) {
      return [
        { label: "Gider Listesi", path: "/expenses" },
        { label: "Gider Başlıkları", path: "/expenses/categories" },
        { label: "Gider Başlığı Düzenle" }
      ];
    }
    if (pathname.startsWith("/expenses/categories")) {
      return [
        { label: "Gider Listesi", path: "/expenses" },
        { label: "Gider Başlıkları" }
      ];
    }
    if (pathname.startsWith("/expenses/add")) {
      return [
        { label: "Gider Listesi", path: "/expenses" },
        { label: "Yeni Gider Ekle" }
      ];
    }
    if (pathname.startsWith("/expenses/edit")) {
      return [
        { label: "Gider Listesi", path: "/expenses" },
        { label: "Gider Düzenle" }
      ];
    }
    if (pathname.startsWith("/expenses")) {
      return [{ label: "Gider Listesi" }];
    }
    if (pathname.startsWith("/finance/settlements/list")) {
      return [
        { label: "Mutabakat İşlemleri", path: "/finance/settlements" },
        { label: "Mutabakat Listesi" }
      ];
    }
    if (pathname.startsWith("/finance/settlements/edit")) {
      return [
        { label: "Mutabakat İşlemleri", path: "/finance/settlements" },
        { label: "Mutabakat Düzenle" }
      ];
    }
    if (pathname.startsWith("/finance/settlements")) {
      return [{ label: "Mutabakat İşlemleri" }];
    }
    if (pathname.startsWith("/finance/collections")) {
      return [{ label: "Tahsilat İşlemleri" }];
    }
    if (pathname.startsWith("/finance/cheques")) {
      return [{ label: "Müşteri Çekleri" }];
    }
    if (pathname.startsWith("/finance/own-cheques")) {
      return [{ label: "Kendi Çeklerimiz" }];
    }
    if (pathname.startsWith("/finance/promissory-notes")) {
      return [{ label: "Müşteri Senetleri" }];
    }
    if (pathname.startsWith("/finance/own-promissory-notes")) {
      return [{ label: "Kendi Senetlerimiz" }];
    }
    if (pathname.startsWith("/finance/installments")) {
      return [{ label: "Taksit Ödeme Planlarım" }];
    }
    if (pathname.startsWith("/finance/accounts/detail")) {
      return [
        { label: "Hesaplarım", path: "/finance/accounts" },
        { label: "Hesap Detayı" }
      ];
    }
    if (pathname.startsWith("/finance/accounts/add")) {
      return [
        { label: "Hesaplarım", path: "/finance/accounts" },
        { label: "Yeni Hesap Ekle" }
      ];
    }
    if (pathname.startsWith("/finance/accounts/edit")) {
      return [
        { label: "Hesaplarım", path: "/finance/accounts" },
        { label: "Hesap Düzenle" }
      ];
    }
    if (pathname.startsWith("/finance/accounts")) {
      return [{ label: "Hesaplarım" }];
    }
    if (pathname === "/" || pathname === "/dashboard") {
      return [{ label: "Kontrol Paneli" }];
    }
    
    // Default fallback: split segments and capitalize
    const segments = pathname.split("/").filter(Boolean);
    return segments.map((seg, idx) => {
      const isLast = idx === segments.length - 1;
      const label = seg.charAt(0).toUpperCase() + seg.slice(1);
      return isLast ? { label } : { label, path: "/" + segments.slice(0, idx + 1).join("/") };
    });
  };

  const breadcrumbs = getBreadcrumbs();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);

  useEffect(() => {
    if (user && branches.length === 0) {
      const fetchBranches = async () => {
        try {
          const res = await BranchService.getAll();
          const branchList = Array.isArray(res) ? res : (res?.data || []);
          setBranches(branchList);
          
          let activeBranchId = localStorage.getItem("activeBranchId");
          if (!activeBranchId) {
            if (user.BranchId) {
              activeBranchId = user.BranchId.toString();
              localStorage.setItem("activeBranchId", activeBranchId);
            } else if (branchList.length > 0) {
              activeBranchId = branchList[0].id.toString();
              localStorage.setItem("activeBranchId", activeBranchId);
            }
          }
          
          if (activeBranchId) {
            setSelectedBranch(parseInt(activeBranchId, 10));
          }
        } catch (err) {
          console.error("Şubeler yüklenemedi", err);
        }
      };
      fetchBranches();
    }
  }, [user, branches.length]);

  const handleBranchChange = (e) => {
    const id = e.value;
    if (id) {
      localStorage.setItem("activeBranchId", id.toString());
      setSelectedBranch(id);
      window.location.reload();
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("activeBranchId");
    setToken(null);
    setUser(null);
    navigate("/login");
  };

  const menuItems = [
    {
      label: "Profilim",
      icon: "pi pi-user",
      command: () => navigate("/profile"),
    },
    {
      label: "Çıkış Yap",
      icon: "pi pi-sign-out",
      command: logout,
    },
  ];

  return (
    <header className="h-[56px] flex items-center justify-between px-4 bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm z-[1060] sticky top-0 transition-all">
      {/* SOL: Menüyü aç/kapat & Arama */}
      <div className="flex items-center gap-3 flex-1">
        <Button
          icon="pi pi-bars"
          className="p-button-text !text-gray-600 hover:!bg-gray-100 !rounded-lg !w-10 !h-10 p-0"
          onClick={onSidebarToggle}
          aria-label="Menüyü Gizle/Göster"
        />

        <div className="hidden md:flex items-center gap-1.5 text-xs font-bold text-slate-400 select-none pl-2">
          {breadcrumbs.map((bc, idx) => {
            const isLast = idx === breadcrumbs.length - 1;
            return (
              <span key={idx} className="flex items-center gap-1.5">
                {bc.path ? (
                  <span
                    className="cursor-pointer hover:text-blue-600 transition-colors"
                    onClick={() => navigate(bc.path)}
                  >
                    {bc.label}
                  </span>
                ) : (
                  <span className="text-slate-700 font-black">
                    {bc.label}
                  </span>
                )}
                {!isLast && <i className="pi pi-chevron-right text-[9px] text-slate-300"></i>}
              </span>
            );
          })}
        </div>
      </div>

      {/* SAĞ: Bildirimler + Kullanıcı + Tenant */}
      <div className="flex items-center gap-2 md:gap-4">
        {branches.length > 0 && (
          <div className="flex items-center gap-1.5 mr-1 sm:mr-2">
            <i className="pi pi-map-marker text-blue-500 text-base" />
            <Dropdown
              value={selectedBranch}
              options={branches}
              optionLabel="name"
              optionValue="id"
              onChange={handleBranchChange}
              placeholder="Şube Seçin"
              className="w-36 sm:w-48 !h-9 !rounded-xl !text-xs sm:!text-sm flex items-center border border-gray-200 focus:!ring-2 focus:!ring-blue-100"
              appendTo="self"
            />
          </div>
        )}

        <div className="flex items-center gap-0.5">
          <div className="relative w-9 h-9 flex items-center justify-center">
            <Button
              icon="pi pi-bell"
              className="p-button-rounded p-button-text !text-gray-500 hover:!bg-gray-50 !w-full !h-full"
              tooltip="Bildirimler"
              tooltipOptions={{ position: 'bottom' }}
              onClick={(e) => op.current.toggle(e)}
            />
            {notificationList.filter(n => !n.isRead).length > 0 && (
              <span className="absolute -top-1.5 -left-1.5 flex h-5 min-w-[20px] px-1 items-center justify-center rounded-full !bg-red-500 text-[10px] font-extrabold !text-white pointer-events-none shadow-md z-[9999]">
                {notificationList.filter(n => !n.isRead).length}
              </span>
            )}
          </div>
          <Button
            icon="pi pi-cog"
            className="p-button-rounded p-button-text !text-gray-500 hover:!bg-gray-50 !w-9 !h-9"
            tooltip="Hızlı Ayarlar"
            tooltipOptions={{ position: 'bottom' }}
            onClick={onSettingsToggle}
          />
        </div>

        <Divider layout="vertical" className="!hidden md:!block !mx-0 !h-8 !border-gray-200" />

        <div
          className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-1.5 rounded-xl transition-all group"
          onClick={(e) => menuRef.current.toggle(e)}
        >
          <div className="hidden md:flex flex-col text-right leading-tight">
            <div className="font-bold text-sm text-gray-800 group-hover:text-blue-600 transition-colors">
              {user?.FullName || "Kullanıcı"}
            </div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
              {user?.TenantName || "Şirket Adı"}
            </div>
          </div>

          <Menu model={menuItems} popup ref={menuRef} />

          <Avatar
            label={user?.FullName?.charAt(0) || "K"}
            className="!bg-blue-600 !text-white !font-bold !rounded-xl shadow-md group-hover:scale-105 transition-transform"
            shape="square"
            size="normal"
          />
        </div>
      </div>

      {/* Bildirim Popover */}
      <OverlayPanel ref={op} style={{ width: '360px' }} className="shadow-xl border border-slate-100 rounded-2xl p-0 z-[1070]">
        <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-800">Bildirimler</span>
            {notificationList.filter(n => !n.isRead).length > 0 && (
              <span className="bg-red-500 text-white font-bold text-[10px] px-1.5 py-0.5 rounded-full">
                {notificationList.filter(n => !n.isRead).length} yeni
              </span>
            )}
          </div>
          {notificationList.filter(n => !n.isRead).length > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-none p-0 cursor-pointer"
            >
              Tümünü Okundu İşaretle
            </button>
          )}
        </div>
        <div className="max-h-[300px] overflow-y-auto py-1">
          {notificationList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 px-4 text-center text-slate-400">
              <i className="pi pi-bell text-2xl mb-2 text-slate-300"></i>
              <span className="text-xs">Henüz bildirim bulunmuyor.</span>
            </div>
          ) : (
            notificationList.map((n) => (
              <div
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`flex gap-2.5 p-3 cursor-pointer transition-colors border-b border-slate-100 last:border-0 ${!n.isRead ? '!bg-blue-50/70 hover:!bg-blue-100/50' : 'hover:bg-slate-50/80'}`}
              >
                <div className="flex-shrink-0 mt-1">
                  {!n.isRead ? (
                    <div className="w-2 h-2 rounded-full bg-blue-600 animate-pulse" />
                  ) : (
                    <div className="w-2 h-2 rounded-full bg-slate-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-bold text-slate-800 ${!n.isRead ? 'font-extrabold' : ''} truncate`}>
                    {n.title}
                  </div>
                  <div
                    className="text-[11px] text-slate-500 mt-0.5"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {stripHtml(n.content)}
                  </div>
                  <div className="text-[9px] font-bold text-slate-400 mt-1.5 flex items-center gap-1">
                    <i className="pi pi-clock text-[8px]"></i>
                    {formatDate(n.createdDate)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </OverlayPanel>

      {/* Bildirim Detay Dialog */}
      <Dialog
        header={selectedNotification?.title || "Bildirim Detayı"}
        visible={notificationDialog}
        style={{ width: '90%', maxWidth: '800px' }}
        onHide={() => setNotificationDialog(false)}
        modal
        className="!rounded-2xl overflow-hidden"
      >
        <div className="flex flex-col gap-4">
          <div className="text-xs text-slate-400 font-bold flex items-center gap-1 border-b border-slate-100 pb-2">
            <i className="pi pi-calendar text-xs"></i>
            Gönderim Tarihi: {formatDate(selectedNotification?.createdDate)}
          </div>
          <div 
            className="text-sm text-slate-700 leading-relaxed rich-text-content"
            dangerouslySetInnerHTML={{ __html: selectedNotification?.content }}
          />
          <div className="flex justify-end mt-2">
            <Button
              label="Kapat"
              severity="info"
              onClick={() => setNotificationDialog(false)}
              className="p-button-sm !rounded-xl"
            />
          </div>
        </div>
      </Dialog>
    </header>
  );
}
